import { Injectable, inject } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, map } from 'rxjs';
import Swal from 'sweetalert2';

import { AlertService } from '@app/shared/alert.service';
import { DecisionRunResult, decisionRunText } from './rental-billing.service';
import type { RentalLifecycleStore } from './rental-lifecycle.store';
import type {
  CancelDecisionDialogData, DecisionDialogResult, LineSheetDialogData, PrescriptionDialogData, RangeDialogData, SaleCancellationDialogData, WindowDialogData,
} from './rental-dialogs.contract';
import { RentalRangeDialogComponent, rangeScopeLayout } from './range-dialog/rental-range-dialog.component';
import { RentalWindowDialogComponent } from './decisions/rental-window-dialog.component';
import { RentalPrescriptionDialogComponent, prescriptionMonths } from './decisions/rental-prescription-dialog.component';
import { RentalSaleCancelDialogComponent } from './decisions/rental-sale-cancel-dialog.component';
import { RentalCancelDecisionDialogComponent } from './decisions/rental-cancel-decision-dialog.component';
import { RentalLineSheetComponent } from './line-sheet/rental-line-sheet.component';

// Refus du serveur quand la ligne a bougé depuis l'affichage (2106 et son champ seen) : la vue est relue.
const STALE_LINE = /la ligne a changé/i;

// Ouvre les dialogues de décision et la fiche de ligne. Anti-cycle : seuls ces dialogues racines et le Planning l'injectent, jamais leurs sous-composants.
@Injectable({ providedIn: 'root' })
export class RentalDecisionDialogsService {
  private dialog = inject(MatDialog);
  private alert = inject(AlertService);

  openRange(d: RangeDialogData): Observable<DecisionDialogResult> {
    return this.open(RentalRangeDialogComponent, d, { width: rangeScopeLayout(d.store.rentalLines()).width, maxWidth: '95vw', maxHeight: '92vh', autoFocus: false, disableClose: true }); // Échap géré par le composant
  }

  openWindow(d: WindowDialogData): Observable<DecisionDialogResult> {
    return this.open(RentalWindowDialogComponent, d, { width: '680px', maxWidth: '95vw', maxHeight: '92vh', autoFocus: false });
  }

  openPrescription(d: PrescriptionDialogData): Observable<DecisionDialogResult> {
    return this.open(RentalPrescriptionDialogComponent, d, { width: { 2: '760px', 3: '960px', 4: '1260px' }[prescriptionMonths()], maxWidth: '95vw', maxHeight: '92vh', autoFocus: false });
  }

  openSaleCancellation(d: SaleCancellationDialogData): Observable<DecisionDialogResult> {
    return this.open(RentalSaleCancelDialogComponent, d, { width: '560px', maxWidth: '95vw', maxHeight: '92vh' });
  }

  openCancelDecision(d: CancelDecisionDialogData): Observable<DecisionDialogResult> {
    return this.open(RentalCancelDecisionDialogComponent, d, { width: '560px', maxWidth: '95vw', maxHeight: '92vh' });
  }

  openLineSheet(d: LineSheetDialogData): Observable<DecisionDialogResult> {
    return this.open(RentalLineSheetComponent, d, { width: '980px', maxWidth: '95vw', maxHeight: '92vh', autoFocus: false });
  }

  // Règle commune des dialogues après postDecisions : rechargement si au moins une acceptée (même suivie d'un refus), puis bilan.
  // Le bilan compte les lignes touchées, pas les évènements (une borne remplacée = ajout + annulation sur une ligne).
  settle(store: RentalLifecycleStore, run: DecisionRunResult, what: string, gender: 'f' | 'm' = 'f'): DecisionDialogResult {
    const saved = run.done.length;
    if (saved > 0) store.decisionsSaved(saved);
    else if (run.failed && STALE_LINE.test(run.failed.message)) store.reload(); // 2106 refusé sur `seen` : l'écran relit la ligne vendue
    if (run.failed) {
      showRunError(decisionRunText(run, (id) => lineLabel(store, id)));
    } else if (saved > 0) {
      const ids = [...new Set(run.done.map((o) => o.event.data.line_id))];
      const noun = ids.every((id) => !store.line(id) && !!store.sale(id)) ? 'vente' : 'matériel';
      this.alert.success(`${what} ${gender === 'm' ? 'enregistré' : 'enregistrée'} sur ${ids.length} ${noun}${ids.length > 1 ? 's' : ''}`);
    }
    return { saved, failed: !!run.failed };
  }

  private open<C, D>(component: ComponentType<C>, data: D, config: MatDialogConfig<D>): Observable<DecisionDialogResult> {
    return this.dialog
      .open<C, D, DecisionDialogResult>(component, { ...config, data })
      .afterClosed()
      .pipe(map((r) => r ?? { saved: 0, failed: false }));
  }
}

// showError rendrait les retours à la ligne du bilan (Enregistrées / Refusée / Non envoyées) comme des espaces.
function showRunError(text: string): void {
  console.error(text);
  Swal.fire({ icon: 'error', title: 'Décision refusée', width: '32em', text, didOpen: (popup) => popup.querySelector<HTMLElement>('.swal2-html-container')?.style.setProperty('white-space', 'pre-line') });
}

export function lineLabel(store: RentalLifecycleStore, lineId: number): string {
  const l = store.line(lineId) ?? store.sale(lineId);
  return l ? `ligne ${l.seq}, ${l.product_name ?? 'article ' + l.product}` : `ligne ${lineId}`;
}
